"use client";

import { Textarea } from "@ui/components/textarea";
import { cn } from "@ui/lib";
import {
	type ChangeEvent,
	type KeyboardEvent,
	type Ref,
	startTransition,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { imageRefAt, insertImageRefs, splitImageRefs } from "../lib/image-refs";

export interface PromptTextareaHandle {
	/** 在光标处（或刚敲的 @ 处）插入「Image 3」，多张用逗号连；插完把光标放到后面 */
	insertImageRefs: (ns: number[]) => void;
	cancelImageRef: () => void;
}

interface PromptTextareaProps {
	/** 默认 prompt；视频工作台用 i2v-prompt（Label、缺字闪框都按它找） */
	id?: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	maxLength?: number;
	className?: string;
	/** 大于 0 时把「Image N」高亮出来（图生图有图时才传） */
	highlightImageCount?: number;
	/** 光标移进 / 移出某个编号时回调，用来给上传区那张描边 */
	onActiveImageRefChange?: (n: number | null) => void;
	/** 敲了 @ 时 true；接着敲别的字时 false */
	onAtTrigger?: (active: boolean) => void;
	onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
	handleRef?: Ref<PromptTextareaHandle>;
}

/**
 * 把提示词输入框从 AiImageGeneratorClient 那近 4,000 行的渲染里隔离出来。
 *
 * 之前每敲一个字都同步 setPrompt → 整个生成器连同历史列表全部重渲染，
 * 是移动端 INP 334ms 的主要来源（2026-09-28）。这里：
 * - 本地态同步更新，光标和 ja/zh-TW 的 IME 组合输入不受影响；
 * - 父级 onChange 放进 startTransition，大树的重渲染可被后续按键打断；
 * - blur 时同步提交一次：点 Generate 前会先触发 blur，保证父级拿到完整文字；
 * - 父级 value 变化（模板回填、reprompt、切 tab、删图改编号）时同步回本地。
 *
 * 编号高亮：输入框还是原生 textarea（字是它自己画的，IME、选中、翻译插件都不受影响），
 * 背后垫一层同尺寸的 div 只画「Image N」的底色，字是透明的；滚动时跟着同步。
 */
export function PromptTextarea({
	id = "prompt",
	value,
	onChange,
	placeholder,
	maxLength,
	className,
	highlightImageCount = 0,
	onActiveImageRefChange,
	onAtTrigger,
	onKeyDown,
	handleRef,
}: PromptTextareaProps) {
	const [local, setLocal] = useState(value);
	// 记住最后一次交给父级的值，避免父级回传同一个值时覆盖用户正在输入的内容
	const lastSentRef = useRef(value);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const backdropRef = useRef<HTMLDivElement>(null);
	// 点弹层里的图时输入框会失焦，插入位置用失焦前记下的选区
	const selectionRef = useRef({ start: value.length, end: value.length });
	// 刚敲的 @ 在哪；选图时把它换成编号
	const atIndexRef = useRef<number | null>(null);
	const [activeRef, setActiveRef] = useState<number | null>(null);
	const highlight = highlightImageCount > 0;

	useEffect(() => {
		if (value !== lastSentRef.current) {
			lastSentRef.current = value;
			setLocal(value);
		}
	}, [value]);

	// 图删光、切走图生图后高亮关掉，上传区的描边也要撤
	useEffect(() => {
		if (!highlight && activeRef !== null) {
			setActiveRef(null);
			onActiveImageRefChange?.(null);
		}
	}, [highlight, activeRef, onActiveImageRefChange]);

	const commit = (next: string, sync: boolean) => {
		lastSentRef.current = next;
		if (sync) {
			onChange(next);
		} else {
			startTransition(() => onChange(next));
		}
	};

	const updateActiveRef = (text: string, caret: number) => {
		const next = highlight
			? imageRefAt(text, caret, highlightImageCount)
			: null;
		if (next !== activeRef) {
			setActiveRef(next);
			onActiveImageRefChange?.(next);
		}
	};

	const rememberSelection = () => {
		const el = textareaRef.current;
		if (!el) {
			return;
		}
		selectionRef.current = {
			start: el.selectionStart,
			end: el.selectionEnd,
		};
		updateActiveRef(el.value, el.selectionStart);
	};

	const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		const next = e.target.value;
		const caret = e.target.selectionStart;
		setLocal(next);
		// "空 ↔ 非空"切换时同步提交：Create 按钮的 disabled 读的是父级 prompt，
		// 走 transition 的话按钮会晚一拍才启用。空框敲一个字立刻点 Create 时，
		// Chromium 不会把点击派发给仍处于 disabled 的按钮，这次点击就丢了
		// （慢 CPU 下可稳定复现，见 tests/prompt-first-click.spec.ts）。
		const emptinessFlipped = !lastSentRef.current.trim() !== !next.trim();
		commit(next, emptinessFlipped);

		if (onAtTrigger) {
			// 只认词首的 @（邮箱、句中的 @ 不弹），且是这一下刚敲进去的
			const typedAt =
				next.length === local.length + 1 &&
				next[caret - 1] === "@" &&
				(caret === 1 || /\s/.test(next[caret - 2]));
			if (typedAt) {
				atIndexRef.current = caret - 1;
				onAtTrigger(true);
			} else if (atIndexRef.current !== null) {
				atIndexRef.current = null;
				onAtTrigger(false);
			}
		}
	};

	useImperativeHandle(handleRef, () => ({
		cancelImageRef: () => {
			atIndexRef.current = null;
		},
		insertImageRefs: (ns) => {
			if (ns.length === 0) {
				return;
			}
			let { start, end } = selectionRef.current;
			const at = atIndexRef.current;
			if (at !== null && local[at] === "@") {
				start = at;
				end = Math.max(end, at + 1);
			}
			start = Math.min(start, local.length);
			end = Math.min(Math.max(end, start), local.length);
			const result = insertImageRefs(local, start, end, ns);
			atIndexRef.current = null;
			setLocal(result.text);
			commit(result.text, true);
			selectionRef.current = { start: result.caret, end: result.caret };
			requestAnimationFrame(() => {
				const el = textareaRef.current;
				if (!el) {
					return;
				}
				el.focus();
				el.setSelectionRange(result.caret, result.caret);
			});
		},
	}));

	const syncScroll = () => {
		if (backdropRef.current && textareaRef.current) {
			backdropRef.current.scrollTop = textareaRef.current.scrollTop;
		}
	};

	return (
		<>
			{highlight && (
				<div
					ref={backdropRef}
					aria-hidden="true"
					// 和 Textarea 的基础样式、传进来的 className 完全一样，保证换行位置对得上；不能带 flex
					className={cn(
						"min-h-[80px] w-full rounded-md border px-3 py-2 text-base md:text-sm",
						className,
						// 滚动条不藏：内容超高时两边同时出滚动条，宽度才一致（Windows 滚动条占 17px）
						"pointer-events-none absolute inset-0 overflow-y-auto whitespace-pre-wrap break-words text-transparent",
					)}
				>
					{splitImageRefs(local, highlightImageCount).map(
						(part, index) =>
							part.ref === undefined ? (
								<span key={index}>{part.text}</span>
							) : part.valid ? (
								<mark
									key={index}
									className={cn(
										"rounded-[5px] text-transparent",
										part.ref === activeRef
											? "bg-primary/25"
											: "bg-primary/10",
									)}
								>
									{part.text}
								</mark>
							) : (
								<span
									key={index}
									className="underline decoration-amber-500 decoration-wavy underline-offset-4"
								>
									{part.text}
								</span>
							),
					)}
					{/* 结尾是换行时补一个零宽字符，否则最后一行不占高度，滚动会差一行 */}
					{local.endsWith("\n") && "​"}
				</div>
			)}
			<Textarea
				id={id}
				ref={textareaRef}
				value={local}
				onChange={handleChange}
				onSelect={rememberSelection}
				onScroll={highlight ? syncScroll : undefined}
				onKeyDown={onKeyDown}
				onBlur={() => {
					rememberSelection();
					commit(local, true);
				}}
				placeholder={placeholder}
				className={cn(
					className,
					highlight && "relative bg-transparent",
				)}
			/>
			{maxLength !== undefined && (
				<span className="absolute bottom-3 right-4 text-xs text-muted-foreground">
					{local.length}/{maxLength}
				</span>
			)}
		</>
	);
}
